/* eslint-disable react/jsx-filename-extension */
/* eslint-disable max-len */

import React, { useCallback, useMemo, useState } from "react";
import BookingFlip from "../components/BookingFlip";

/* Theme color */
const PRIMARY_GREEN = "#7FBB42";

/* ---------- Helpers ---------- */
function getMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonthEnd = new Date(year, month, 0).getDate();
  const prev = [];
  for (let i = startDay - 1; i >= 0; i -= 1) {
    prev.push({
      date: new Date(year, month - 1, prevMonthEnd - i),
      day: prevMonthEnd - i,
      inMonth: false,
    });
  }

  const curr = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    curr.push({
      date: new Date(year, month, d),
      day: d,
      inMonth: true,
    });
  }

  const total = prev.length + curr.length;
  const remain = (7 - (total % 7)) % 7;
  const next = [];
  for (let i = 1; i <= remain; i += 1) {
    next.push({
      date: new Date(year, month + 1, i),
      day: i,
      inMonth: false,
    });
  }

  return [...prev, ...curr, ...next];
}

function generateTimeset(start = 9, end = 18, step = 15) {
  const slots = [];
  for (let m = 0; m < (end - start) * 60; m += step) {
    const d = new Date();
    d.setHours(start + Math.floor(m / 60), m % 60, 0, 0);
    slots.push(new Date(d));
  }
  return slots;
}

function formatTime(dt) {
  let hours = dt.getHours();
  const minutes = dt.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${ampm}`;
}

/* ---------- Component ---------- */
export default function BookConsultation() {
  // ✅ Today (without time)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedTime, setSelectedTime] = useState(null);
  const [duration, setDuration] = useState(15);

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);
  const times = useMemo(() => generateTimeset(), []);

  /* ---------- Calendar ---------- */
  const prevMonth = useCallback(() => {
    setViewDate(new Date(year, month - 1, 1));
  }, [year, month]);

  const nextMonth = useCallback(() => {
    setViewDate(new Date(year, month + 1, 1));
  }, [year, month]);

  const handleDayClick = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  /* ---------- Duration ---------- */
  const handleDurationClick = useCallback((value) => {
    setDuration(value);
  }, []);

  /* ---------- Time ---------- */
  const handleTimeClick = useCallback((time) => {
    setSelectedTime(time);
  }, []);

  /* ---------- API CALL ---------- */
  const sendBooking = useCallback(
    async ({ email, firstName, lastName }) => {
      const payload = {
        firstName,
        lastName,
        email,
        date: selectedDate.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        time: formatTime(selectedTime),
        duration: `${duration} minutes`,
      };

      const res = await fetch("/api/book-consultation.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data?.message || "Booking failed");
      }
    },
    [selectedDate, selectedTime, duration],
  );

  /* ---------- Confirm ---------- */
  const handleConfirmBooking = useCallback(
    async ({ email, firstName, lastName }) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(email)) {
        setError("❌ Please enter a valid email address.");
        return;
      }

      setSending(true);
      setError("");
      setSuccess(false);

      try {
        await sendBooking({ email, firstName, lastName });
        setSuccess(true);
        setSelectedTime(null);
      } catch (err) {
        setError(err.message || "❌ Failed to book.");
      } finally {
        setSending(false);
      }
    },
    [sendBooking],
  );

  const handleCloseFlip = useCallback(() => {
    setSelectedTime(null);
  }, []);

  /* ---------- UI ---------- */
  return (
    <main className="max-w-6xl mx-auto my-12 px-4">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="flex flex-col lg:flex-row">

          {/* LEFT — Calendar */}
          <div
            className="lg:w-1/2 p-8 text-white"
            style={{ backgroundColor: PRIMARY_GREEN }}
          >
            <div className="flex justify-between mb-6">
              <button type="button" onClick={prevMonth}>‹</button>
              <div className="font-bold">
                {viewDate.toLocaleString(undefined, { month: "long" })} {year}
              </div>
              <button type="button" onClick={nextMonth}>›</button>
            </div>

            <div className="grid grid-cols-7 gap-2 text-xs mb-3">
              {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
                <div key={d} className="text-center">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {monthDays.map((d) => {
                const cellDate = new Date(d.date);
                cellDate.setHours(0, 0, 0, 0);

                const isPast = cellDate < today;
                const isSelected = d.inMonth && cellDate.getTime() === selectedDate.getTime();

                return (
                  <button
                    key={d.date.toISOString()}
                    type="button"
                    disabled={isPast}
                    onClick={() => !isPast && handleDayClick(cellDate)}
                    className={`h-10 rounded-full transition
                      ${isSelected ? "bg-white/30 ring-2 ring-white/40" : ""}
                      ${isPast ? "opacity-30 cursor-not-allowed" : "hover:bg-white/20"}
                      ${d.inMonth ? "" : "opacity-40"}
                    `}
                  >
                    {d.day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT */}
          <div className="lg:w-1/2 p-8">
            {success && (
              <div className="bg-green-50 border border-green-300 text-green-800 rounded-xl p-6 text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Booking Confirmed</h3>
                <p>We’ll contact you shortly.</p>
              </div>
            )}

            {error && (
              <p className="text-red-600 text-center mb-4">{error}</p>
            )}

            {selectedTime === null ? (
              <>
                <h3 className="text-xl font-semibold mb-4">How long?</h3>

                <div className="flex gap-3 mb-6 flex-wrap">
                  {[15, 30, 45, 60].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => handleDurationClick(d)}
                      className={`px-4 py-2 rounded-lg border transition ${
                        duration === d
                          ? "bg-green-500 text-white border-green-500"
                          : "bg-white border-gray-300 text-gray-700 hover:border-green-500"
                      }`}
                    >
                      {d === 60 ? "1 hour" : `${d} mins`}
                    </button>
                  ))}
                </div>

                <div className="h-64 overflow-y-auto space-y-2">
                  {times.map((t) => (
                    <button
                      key={t.toISOString()}
                      type="button"
                      onClick={() => handleTimeClick(t)}
                      className="w-full border rounded py-3 text-center text-green-600 hover:border-green-500"
                    >
                      {formatTime(t)}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <BookingFlip
                initialDate={selectedDate}
                selectedTime={selectedTime}
                duration={duration}
                loading={sending}
                onConfirm={handleConfirmBooking}
                onClose={handleCloseFlip}
              />
            )}
          </div>

        </div>
      </div>
    </main>
  );
}
